/**
 * Nathalie Robert (assistante administrative) — discrete board prep memo.
 *
 * Fires 5 days BEFORE a board meeting (mid-year or annual) IF and only IF
 * Nathalie has been hired. Pushes a short, in-character Messagerie note
 * reminding the patron and asking which files she should prepare.
 *
 * Trigger anchors (handled by `coreSlice.nextDay`) :
 *   • Mid-year prep memo: M6 D25 (Jun 25)   — 5 days before M6 D30 board
 *   • Annual prep memo:   M12 D25 (Dec 25)  — 5 days before M12 D30 board
 *
 * Returns the Messagerie payload OR null when conditions are not met.
 *
 * 31/05/2026 — narrative bonus : Nathalie becomes an ACTIVE assistant
 *              (preparation, not just synthesis).
 */

const TTL_DAYS = 14;

/**
 * @param {object} state    live store state (must expose hiredSupport,
 *                          totalDays, year).
 * @param {'midyear'|'annual'} kind  which board the memo is preparing for.
 * @returns {object|null}   Messagerie payload OR null.
 */
export function buildNathalieBoardMemo(state, kind) {
  if (!state || typeof state !== 'object') return null;
  if (kind !== 'midyear' && kind !== 'annual') return null;
  const hired = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
  if (!hired) return null;

  const year = state.year || 1;
  const totalDays = state.totalDays || 0;

  if (kind === 'midyear') {
    return {
      senderType: 'support',
      senderName: 'Nathalie Robert (Assistante)',
      senderRole: 'role-nathalie-robert',
      tone: 'friendly',
      kind: 'info',
      subject: `Conseil de mi-année — dans 5 jours`,
      body:
        `Bonjour Patron,\n\n` +
        `Petit rappel discret : le **Conseil d'administration de mi-année** se tient dans 5 jours (le 30 juin).\n\n` +
        `Je préparais à sortir : le **bilan S1 ${year}** (CA, charges, marge), la **comparaison aux objectifs**, et le **point trésorerie/runway** que le Président voudra voir.\n\n` +
        `Vous voulez que j'ajoute un point particulier au dossier ? Sinon je laisse comme d'habitude.\n\n` +
        `— Nathalie`,
      ttlDays: TTL_DAYS,
      receivedDay: totalDays,
    };
  }

  // kind === 'annual'
  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'friendly',
    kind: 'info',
    subject: `Clôture annuelle — dans 5 jours`,
    body:
      `Bonjour Patron,\n\n` +
      `Note de préparation : le **Conseil d'administration annuel** se tient dans 5 jours (le 30 décembre).\n\n` +
      `J'ai déjà commencé à classer : le **bilan 12 mois** de l'exercice ${year}, les **résolutions de l'année dernière** (pour suivi), le **dossier IS prévisionnel**${state.taxAutoProvisioningEnabled ? ' (provisionnement Midal à jour)' : ''}, et le récap des **chauffeurs Top/Bottom** pour les évaluations.\n\n` +
      `Le Président aime aussi avoir le **fichier clients clés** et le **planning S1 ${year + 1}**. Je les inclus par défaut, sauf contre-ordre.\n\n` +
      `— Nathalie`,
    ttlDays: TTL_DAYS,
    receivedDay: totalDays,
  };
}
